// ─────────────────────────────────────────────────────────────────────────────
// Horário comercial e feriados.
//
// Usado na previsão do "Contato sem sucesso": janela 08:30–17:30, segunda a
// sexta, exceto feriados nacionais (fixos + móveis). Os móveis saem da Páscoa:
// Sexta-feira Santa, segunda e terça de Carnaval e Corpus Christi.
//
// Para incluir feriados municipais/estaduais fixos, acrescente 'MM-DD' em
// FERIADOS_FIXOS_EXTRA.
// ─────────────────────────────────────────────────────────────────────────────

const FERIADOS_FIXOS_EXTRA = [];

const pad = n => String(n).padStart(2, '0');

/** Data em ISO local (YYYY-MM-DD), sem passar por UTC. */
export function dataLocalISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Domingo de Páscoa do ano — algoritmo de Meeus/Jones/Butcher. */
export function calcularPascoa(ano) {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(ano, mes - 1, dia);
}

// O cálculo dos móveis não muda dentro do mesmo ano — calcula uma vez e guarda.
const _feriadosCache = {};

/** Conjunto de datas ISO que são feriado no ano. */
export function feriadosDoAno(ano) {
  if (_feriadosCache[ano]) return _feriadosCache[ano];
  const set = new Set();

  // Fixos nacionais (+ extras configuráveis)
  ['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25']
    .concat(FERIADOS_FIXOS_EXTRA)
    .forEach(md => set.add(`${ano}-${md}`));

  // Móveis (a partir da Páscoa)
  const pascoa = calcularPascoa(ano);
  const offset = dias => { const dt = new Date(pascoa); dt.setDate(dt.getDate() + dias); return dt; };
  [-2, -48, -47, 60].forEach(off => {   // Sexta Santa, seg/ter Carnaval, Corpus Christi
    set.add(dataLocalISO(offset(off)));
  });

  _feriadosCache[ano] = set;
  return set;
}

/** true se for dia útil: não é sábado, domingo nem feriado. */
export function ehDiaUtil(d) {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  return !feriadosDoAno(d.getFullYear()).has(dataLocalISO(d));
}

/**
 * Empurra um horário para dentro da janela comercial.
 * Antes das 08:30 num dia útil → 08:30 do mesmo dia.
 * Depois das 17:30, fim de semana ou feriado → 08:30 do próximo dia útil.
 */
export function ajustarParaHorarioComercial(base) {
  const d = new Date(base);
  const ABERTURA_MIN = 8 * 60 + 30;      // 08:30
  const FECHAMENTO_MIN = 17 * 60 + 30;   // 17:30

  const proximoDiaUtil0830 = dt => {
    do { dt.setDate(dt.getDate() + 1); } while (!ehDiaUtil(dt));
    dt.setHours(8, 30, 0, 0);
  };

  if (!ehDiaUtil(d)) { proximoDiaUtil0830(d); return d; }

  const minutos = d.getHours() * 60 + d.getMinutes();
  if (minutos < ABERTURA_MIN) d.setHours(8, 30, 0, 0);
  else if (minutos > FECHAMENTO_MIN) proximoDiaUtil0830(d);

  return d;
}

/** Data (YYYY-MM-DD) e hora (HH:MM:SS) do MySuite prontas para exibir. */
export function formatarPrevisao(data, hora) {
  const temData = data && data !== '0000-00-00';
  const temHora = hora && hora !== '00:00:00';
  if (!temData && !temHora) return 'Não definida';

  let texto = '';
  if (temData) {
    const [ano, mes, dia] = data.split('-');
    texto += `${dia}/${mes}/${ano}`;
  }
  if (temHora) texto += (texto ? ' às ' : '') + hora.substring(0, 5);
  return texto;
}

/** 'HH:MM:SS' (Postgres) → minutos desde a meia-noite. null quando vazio. */
export function horaParaMinutos(hhmmss) {
  if (!hhmmss) return null;
  const partes = String(hhmmss).split(':');
  return Number(partes[0]) * 60 + Number(partes[1]);
}

/** Minutos → "1h 20min" / "45min". */
export function formatarDuracaoMin(min) {
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h ${m}min` : `${h}h`;
}

/** Data ISO curta (YYYY-MM-DD ou ISO completo) → DD/MM/AAAA. */
export function fmtDataSimples(str) {
  if (!str) return '—';
  const iso = String(str).slice(0, 10);
  const [ano, mes, dia] = iso.split('-');
  if (!ano || !mes || !dia) return String(str);
  return `${dia}/${mes}/${ano}`;
}

/** Timestamp ISO → DD/MM/AAAA HH:MM. */
export function fmtDataHora(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return String(str);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
